import React, { useEffect, useState } from "react";
import {
  CheckIcon,
  ExclamationCircleIcon,
} from "@heroicons/react/20/solid";

import Input from "../../../controls/Input";
import { useCellSaveState } from "../../../../hooks/useCellSaveState";

function SaveStatusIndicator({ status }) {
  const saveStatusLabel = {
    saving: "Saving",
    saved: "Saved",
    error: "Save failed",
  }[status];

  const saveStatusIndicator = {
    saving: (
      <span
        className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-gray-300 border-t-indigo-600"
        aria-hidden="true"
      />
    ),
    saved: <CheckIcon className="h-5 w-5 text-green-800" aria-hidden="true" />,
    error: (
      <ExclamationCircleIcon
        className="h-4 w-4 text-red-600"
        aria-hidden="true"
      />
    ),
  }[status];

  return (
    <span
      className="inline-flex h-5 w-5 shrink-0 items-center justify-center"
      role={saveStatusLabel ? "status" : undefined}
      aria-label={saveStatusLabel}
      title={saveStatusLabel}
    >
      {saveStatusIndicator}
    </span>
  );
}

export default function ProductPriceRulesTab(props) {
  const { product, updateProductPriceRulePrice } = props;

  const [rows, setRows] = useState([]);
  const { getSaveStatus, saveCell } = useCellSaveState();

  const defaultProductPrice = () => {
    const price = product?.price;
    if (price === "" || price === null || price === undefined) {
      return 0;
    }
    return price;
  };

  useEffect(() => {
    setRows(
      (product?.price_rule_prices || []).map((row) => ({
        ...row,
        availability: Boolean(row.availability),
      }))
    );
  }, [product]);

  const normalizePrice = (rawValue) => {
    if (rawValue === "" || rawValue === null || rawValue === undefined) {
      return null;
    }
    return rawValue;
  };

  const persistRow = (priceRuleId, availability, unitPrice) => {
    if (!product?.id || !updateProductPriceRulePrice) {
      return Promise.resolve({ success: false });
    }

    const key = String(priceRuleId);
    return saveCell(key, async () => {
      const success = await updateProductPriceRulePrice(
        product.id,
        priceRuleId,
        availability,
        unitPrice
      );
      return { success: Boolean(success) };
    });
  };

  const handleAvailabilityChange = (priceRuleId, checked) => {
    const current = rows.find((row) => row.price_rule_id === priceRuleId);
    let unitPrice = normalizePrice(current?.unit_price);

    if (checked && unitPrice === null) {
      unitPrice = defaultProductPrice();
    }

    setRows((prev) =>
      prev.map((row) =>
        row.price_rule_id === priceRuleId
          ? {
              ...row,
              availability: checked,
              unit_price: unitPrice,
            }
          : row
      )
    );

    persistRow(priceRuleId, checked, unitPrice);
  };

  const handlePriceChange = (priceRuleId, rawValue) => {
    setRows((prev) =>
      prev.map((row) =>
        row.price_rule_id === priceRuleId
          ? {
              ...row,
              unit_price: rawValue,
              // Entering a price auto-enables the rule
              availability:
                rawValue !== "" && rawValue !== null && rawValue !== undefined
                  ? true
                  : row.availability,
            }
          : row
      )
    );
  };

  const handlePriceBlur = (priceRuleId, rawValue) => {
    const current = rows.find((row) => row.price_rule_id === priceRuleId);
    let unitPrice = normalizePrice(rawValue);
    let availability = Boolean(current?.availability);

    // Entering a price auto-enables
    if (unitPrice !== null) {
      availability = true;
    }

    // Available always needs an explicit price
    if (availability && unitPrice === null) {
      unitPrice = defaultProductPrice();
    }

    // Unavailable with empty price: keep prior value, no-op if never set
    if (!availability && unitPrice === null) {
      const existing = normalizePrice(current?.unit_price);
      if (existing === null) {
        return;
      }
      unitPrice = existing;
    }

    setRows((prev) =>
      prev.map((row) =>
        row.price_rule_id === priceRuleId
          ? { ...row, availability, unit_price: unitPrice }
          : row
      )
    );

    persistRow(priceRuleId, availability, unitPrice);
  };

  if (!rows.length) {
    return (
      <div className="px-4 py-6">
        <h3 className="text-sm font-semibold text-gray-900">
          Price rule prices
        </h3>
        <p className="mt-2 text-sm text-gray-500">
          No price rules have been created yet. Create price rules under Setup,
          then enable products and set prices here.
        </p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200">
      <div className="px-6 py-4 border-b border-gray-200">
        <h3 className="text-lg font-semibold text-gray-900">
          Price Rule Management
        </h3>
        <p className="text-sm text-gray-600 mt-1">
          Enable a product on a price rule with an explicit price (defaults to
          the product unit price). Unchecking keeps the price for sales
          visibility but marks it unavailable. Changes save when you toggle
          availability or leave a price field.
        </p>
        <p className="text-sm font-medium text-gray-900 mt-2">
          Current unit price: ${defaultProductPrice()}
        </p>
      </div>

      <div className="p-6">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th
                  scope="col"
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                >
                  Price Rule
                </th>
                <th
                  scope="col"
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                >
                  Available
                </th>
                <th
                  scope="col"
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                >
                  Price
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {rows.map((row) => {
                const saveStatus = getSaveStatus(String(row.price_rule_id));
                return (
                  <tr
                    key={row.price_rule_id}
                    className="hover:bg-gray-50 transition-colors duration-150"
                  >
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">
                        {row.name || `Rule #${row.price_rule_id}`}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <input
                        id={`availability-${row.price_rule_id}`}
                        name={`availability-${row.price_rule_id}`}
                        type="checkbox"
                        checked={Boolean(row.availability)}
                        className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                        onChange={(e) =>
                          handleAvailabilityChange(
                            row.price_rule_id,
                            e.target.checked
                          )
                        }
                      />
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        <Input
                          labelOn={false}
                          name={`price_rule_${row.price_rule_id}`}
                          type="number"
                          className="mt-1 block w-28 border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none sm:text-sm"
                          onChange={(e) =>
                            handlePriceChange(
                              row.price_rule_id,
                              e.target.value
                            )
                          }
                          onBlur={(e) =>
                            handlePriceBlur(
                              row.price_rule_id,
                              e.target.value
                            )
                          }
                          value={
                            row.unit_price === null ||
                            row.unit_price === undefined
                              ? ""
                              : row.unit_price
                          }
                        />
                        {saveStatus !== "idle" ? (
                          <SaveStatusIndicator status={saveStatus} />
                        ) : (
                          <span
                            className="inline-flex h-5 w-5 shrink-0"
                            aria-hidden="true"
                          />
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
